import "./tournament-message-send-confirmation.scss";
import { useEffect, useState } from "react";
import { GSForm, GSSidePanelPage } from "golfstatus_react_components";
import { faChevronLeft, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearNotifications,
  sendMessage,
  selectCurrentMessage,
  selectCurrentMessageRecipientCount,
  selectCurrentMessageRecipientGroups,
  selectSelectedMessageTournaments,
  selectMessagesNotifications,
} from "../../../reducers/messagesSlice";
import { selectCurrentTournament } from "../../../reducers/tournamentSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useNotificationBanner,
  useNotificationNavigation,
} from "../../../hooks/notificationHooks";
import { useRefreshAllRecipients } from "../../../hooks/messageRefreshHooks";
import { confirmationForm } from "../../../forms/ConfirmationForms";
import TournamentMessageConfirmationItem from "./tournament-message-confirmation-item";

const TournamentMessageSendConfirmation = () => {
  const [isValid, setIsValid] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch(
    "/:tournamentID/messages/:messageID/send-confirmation",
  );

  const currentMessage = useSelector(selectCurrentMessage);
  const recipientCount = useSelector(selectCurrentMessageRecipientCount);
  const recipientGroups = useSelector(selectCurrentMessageRecipientGroups);
  const currentTournament = useSelector(selectCurrentTournament);
  const tournaments = useSelector((state) =>
    selectSelectedMessageTournaments(
      state,
      match?.params?.tournamentID,
      currentTournament,
    ),
  );
  const notifications = useSelector(selectMessagesNotifications);

  const { refreshing, refresh, loading } = useRefreshAllRecipients({
    tournamentID: match?.params?.tournamentID,
    messageID: match?.params?.messageID,
    autoRefresh: true,
  });

  useEffect(() => {
    dispatch(clearNotifications());
  }, [dispatch, match?.params?.tournamentID, match?.params?.messageID]);

  useEffect(() => {
    setIsValid(recipientCount > 0);
  }, [recipientCount]);

  const organizationName =
    tournaments.find((t) => t?.id === match?.params?.tournamentID)?.organization
      ?.name ?? currentTournament?.organization?.name;

  const messageType =
    currentMessage?.channel === "email" ? "email" : "app notification";

  useNotificationNavigation(
    "saved",
    isSending ? notifications : [],
    `/${match?.params?.tournamentID}/messages/${match?.params?.messageID}/sent-success`,
    "Message",
  );

  const handleSend = () => {
    setIsValid(false);
    setIsSending(true);
    dispatch(
      sendMessage({
        tournamentID: match?.params?.tournamentID,
        messageID: match?.params?.messageID,
      }),
    );
  };

  const goBack = () => {
    dispatch(clearNotifications());
    navigate(-1);
  };

  const getNavigation = () => {
    return {
      title: "Send Message",
      leftIcon: faChevronLeft,
      leftButtonClick: goBack,
    };
  };

  const getFormTitle = () => {
    return (
      <div className="confirm-header">
        <div className="icon" style={{ color: "#000000" }}>
          <FontAwesomeIcon icon={faPaperPlane} />
        </div>
        Send Message
      </div>
    );
  };

  const getContent = () => {
    return (
      <GSForm
        formTitle={getFormTitle()}
        formSections={confirmationForm(
          `Are you sure you want to send this ${messageType}?`,
          <TournamentMessageConfirmationItem
            currentMessage={currentMessage}
            tournaments={tournaments}
            recipientGroups={recipientGroups}
            organizationName={organizationName}
            refreshing={refreshing}
            onRefresh={refresh}
            showRefresh={true}
          />,
        )}
      />
    );
  };

  const getDrawerActions = () => {
    return [
      {
        name: "Send Message",
        isDisabled: !isValid,
        action: handleSend,
        type: "black",
      },
      {
        name: "Cancel",
        action: goBack,
        type: "grey",
      },
    ];
  };

  const getDrawer = () => {
    return { actions: getDrawerActions() };
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const [banner] = useNotificationBanner({ notifications, timeoutAction });

  return (
    <tournament-message-send-confirmation>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        loading={loading}
        loadingText="Preparing for Review..."
        content={getContent()}
        drawer={getDrawer()}
      />
    </tournament-message-send-confirmation>
  );
};

export default TournamentMessageSendConfirmation;
